import React, { Suspense, useMemo } from 'react';
import Fuse from 'fuse.js';
import {
  Card,
  Tag,
  Modal,
  Skeleton,
  Badge,
  Alert,
  Typography,
  Space,
  Button,
  Empty
} from 'antd';
import { useNavigate } from 'react-router-dom';
import Icon from '@ant-design/icons';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import {
  AvailableIntegration,
  availableIntegrations
} from '@pages/AdminSettings/components/IntegrationTab/constants';
import { IntegrationItemDto, OrganizationItemDto } from '@api/Api';

const ORG_IDS_TO_DISPLAY_PLUGINS = [
  '4898d649-7e8a-4764-b35a-b0a92f254a6f',
  '269d0a58-e1b8-47b7-a380-b38690533007',
  'f8996032-0805-4599-b050-095ee3fac285'
];

const { Title, Paragraph } = Typography;

const integrationsList = Object.values(availableIntegrations);
const fuseSearch = new Fuse(integrationsList, {
  keys: ['name', 'description', 'tags'],
  threshold: 0.3
});

interface AvailableIntegrationsProps {
  readonly searchQuery: string;
  readonly organizationId: string;
  readonly organizationFeatures: Pick<
    OrganizationItemDto['features'],
    'basicIntegrations' | 'advancedIntegrations'
  >;
  readonly installedIntegrations: IntegrationItemDto[];
  readonly refetchInstalledIntegrations: () => void;
}

export default function AvailableIntegrations({
  searchQuery,
  organizationId,
  organizationFeatures,
  installedIntegrations,
  refetchInstalledIntegrations
}: AvailableIntegrationsProps) {
  const navigate = useNavigate();
  const integrations = useMemo(() => {
    const filteredIntegrations = searchQuery
      ? fuseSearch.search(searchQuery).map((result) => result.item)
      : integrationsList;

    return filteredIntegrations.filter((integration) => {
      if (
        integration.partiallySupported &&
        !ORG_IDS_TO_DISPLAY_PLUGINS.includes(organizationId)
      ) {
        return false;
      }

      return true;
    });
  }, [searchQuery, organizationId]);

  const [installInstructionsOpen, setInstallInstructionsOpen] = React.useState<{
    type: AvailableIntegration['type'];
    key: string;
  }>();
  const currentIntegration = useMemo(
    () =>
      installInstructionsOpen
        ? {
            ...availableIntegrations[installInstructionsOpen.type],
            key: installInstructionsOpen.key
          }
        : undefined,
    [installInstructionsOpen]
  );

  const [alreadyInstalledWarning, setAlreadyInstalledWarning] =
    React.useState<AvailableIntegration['type']>();
  const installedIntegration = useMemo(
    () =>
      alreadyInstalledWarning
        ? availableIntegrations[alreadyInstalledWarning]
        : undefined,
    [alreadyInstalledWarning]
  );

  const isIntegrationAvailableByPlan = (integration: AvailableIntegration) => {
    return integration.tier === 'basic'
      ? organizationFeatures.basicIntegrations
      : organizationFeatures.advancedIntegrations;
  };

  const getUpgradePromptText = (integration: AvailableIntegration) => {
    return integration.tier === 'basic'
      ? ' to a Team plan to install this integration.'
      : ' to an Enterprise plan to install this integration.';
  };

  if (!integrations.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No available integrations found"
        style={{
          gridColumnStart: 1,
          gridColumnEnd: -1
        }}
      />
    );
  }

  return (
    <>
      {integrations.map((x) => {
        const isInstalled = installedIntegrations.some(
          (i) => i.type === x.type
        );
        const needsUpgrade = !isIntegrationAvailableByPlan(x);
        const showUpgradePrompt = !x.comingSoon && needsUpgrade;
        const card = (
          <Card
            key={x.type}
            hoverable={!!x.installInstructions && !needsUpgrade}
            style={{
              height: '100%',
              cursor: showUpgradePrompt ? 'not-allowed' : 'pointer'
            }}
            onClick={() => {
              if (isInstalled && !showUpgradePrompt) {
                setAlreadyInstalledWarning(x.type);
              } else if (!showUpgradePrompt && x.installInstructions) {
                setInstallInstructionsOpen({
                  type: x.type,
                  key: crypto.randomUUID()
                });
              }
            }}
          >
            <Card.Meta
              title={x.name}
              description={
                <>
                  <Paragraph>
                    {x.tags.map((x) => (
                      <Tag key={x} style={{ marginTop: 8 }}>
                        {x}
                      </Tag>
                    ))}
                  </Paragraph>
                  <Paragraph>{x.description}</Paragraph>
                </>
              }
              avatar={x.logo ? <x.logo width={64} height={64} /> : null}
            />
          </Card>
        );
        if (showUpgradePrompt) {
          return (
            <Badge.Ribbon
              key={x.type}
              text={
                <UpgradePlanTooltip text={getUpgradePromptText(x)} isCanVisible>
                  <Space align="center" size={4}>
                    <Icon component={UpgradePlanSvg} />
                    Upgrade Required
                  </Space>
                </UpgradePlanTooltip>
              }
              color="#353839"
            >
              {card}
            </Badge.Ribbon>
          );
        }
        return isInstalled ? (
          <Badge.Ribbon key={x.type} text="Installed" color="green">
            {card}
          </Badge.Ribbon>
        ) : x.comingSoon ? (
          <Badge.Ribbon key={x.type} text="Coming Soon" color="geekblue">
            {card}
          </Badge.Ribbon>
        ) : (
          card
        );
      })}

      <Modal
        open={!!currentIntegration}
        onCancel={() => {
          refetchInstalledIntegrations();
          setInstallInstructionsOpen(undefined);
        }}
        footer={null}
        centered
        width={800}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'min(85vh, 1024px)',
            minHeight: 0
          }}
        >
          <Suspense
            fallback={
              <>
                <Skeleton />
                <Skeleton />
                <Skeleton />
                <Skeleton />
              </>
            }
          >
            {currentIntegration?.installInstructions ? (
              <currentIntegration.installInstructions
                key={currentIntegration.key}
                organizationId={organizationId}
                uniqueKey={currentIntegration.key}
                onClose={() => {
                  refetchInstalledIntegrations();
                  setInstallInstructionsOpen(undefined);
                }}
              />
            ) : null}
          </Suspense>
        </div>
      </Modal>
      <Modal
        open={!!installedIntegration}
        footer={null}
        onCancel={() => {
          refetchInstalledIntegrations();
          setAlreadyInstalledWarning(undefined);
        }}
        width={560}
        destroyOnClose
      >
        <Title level={3} style={{ marginBottom: 8 }}>
          {installedIntegration?.allowMultiple
            ? 'Already installed – add another?'
            : 'Already installed'}
        </Title>

        {installedIntegration?.allowMultiple ? (
          <Alert
            type="info"
            showIcon
            message={`${installedIntegration?.name} is already installed.`}
            description={
              <Paragraph style={{ margin: '8px 0 0' }}>
                If you need a separate instance (for a different workspace or
                test environment), click <strong>Add another</strong>. Otherwise
                choose <em>Cancel</em>.
              </Paragraph>
            }
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            message={`${installedIntegration?.name} is already installed.`}
            description={
              <Paragraph style={{ margin: '8px 0 0' }}>
                You can only have one instance of this integration. Manage or
                configure it from the <em>Installed Integrations</em> list.
              </Paragraph>
            }
          />
        )}

        <Space
          style={{ marginTop: 24, justifyContent: 'flex-end', width: '100%' }}
        >
          <Button
            onClick={() => {
              refetchInstalledIntegrations();
              setAlreadyInstalledWarning(undefined);
            }}
          >
            Cancel
          </Button>

          {installedIntegration?.allowMultiple ? (
            <Button
              type="primary"
              onClick={() => {
                setAlreadyInstalledWarning(undefined);
                setInstallInstructionsOpen({
                  type: installedIntegration.type,
                  key: crypto.randomUUID()
                });
              }}
            >
              Add another
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={() => {
                refetchInstalledIntegrations();
                setAlreadyInstalledWarning(undefined);
                navigate('integrations/installed');
              }}
            >
              Go to installed integrations
            </Button>
          )}
        </Space>
      </Modal>
    </>
  );
}
